import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { timeAgo, LANGUAGES } from "@/lib/format";
import {
  Plus, Sparkle, Lightning, PaperPlaneTilt, Trash, PencilSimple,
  CalendarPlus, SignOut, SignIn, ClockCountdown, Cake, FileText, ArrowRight,
  DeviceMobile, Star, EnvelopeSimple, Translate,
} from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

const TRIGGERS = {
  reservation_created: { label: "New reservation", desc: "Fires when a guest books a stay", icon: CalendarPlus, color: "text-blue-600 bg-blue-50" },
  check_in: { label: "Guest checks in", desc: "Fires when a guest checks in — great for in-stay comfort checks", icon: SignIn, color: "text-emerald-600 bg-emerald-50" },
  check_out: { label: "After check-out", desc: "Fires when a guest completes their stay", icon: SignOut, color: "text-violet-600 bg-violet-50" },
  inactive_90: { label: "90 days inactive", desc: "Fires for lapsed guests to win them back", icon: ClockCountdown, color: "text-amber-600 bg-amber-50" },
  birthday: { label: "Birthday / anniversary", desc: "Fires on a guest's special day", icon: Cake, color: "text-pink-600 bg-pink-50" },
};

const channelMeta = {
  email: { icon: EnvelopeSimple, name: "Email", color: "bg-primary/10 text-primary" },
  sms: { icon: DeviceMobile, name: "SMS", color: "bg-blue-50 text-blue-600" },
  survey: { icon: Star, name: "Survey", color: "bg-amber-50 text-amber-600" },
};

const fmtDelay = (s) => {
  const v = s.delay_value ?? s.delay_days ?? 0;
  const u = s.delay_unit || "days";
  if (v === 0) return "Immediately";
  return u === "hours" ? `${v}h after` : `${v}d after`;
};

const typeBadge = {
  "pre-arrival": "bg-blue-50 text-blue-600 border-blue-200",
  "post-stay": "bg-violet-50 text-violet-600 border-violet-200",
  "win-back": "bg-amber-50 text-amber-600 border-amber-200",
  birthday: "bg-pink-50 text-pink-600 border-pink-200",
  promotion: "bg-primary/10 text-primary border-primary/20",
  newsletter: "bg-secondary text-muted-foreground border-border",
};

export default function Automations() {
  const qc = useQueryClient();
  const { data: automations = [] } = useQuery({ queryKey: ["automations"], queryFn: async () => (await api.get("/automations")).data });
  const { data: templates = [] } = useQuery({ queryKey: ["templates"], queryFn: async () => (await api.get("/templates")).data });
  const { data: segments = [] } = useQuery({ queryKey: ["segments"], queryFn: async () => (await api.get("/segments")).data });

  const activeCount = automations.filter((a) => a.active).length;
  const totalSent = automations.reduce((a, c) => a + (c.sent_count || 0), 0);

  return (
    <Page>
      <PageHeader title="Lifecycle Automations" subtitle="Set it once — the right email fires automatically at the right moment." />

      <Tabs defaultValue="automations">
        <TabsList className="mb-5">
          <TabsTrigger value="automations" data-testid="tab-automations">Automations</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="automations">
          <div className="grid grid-cols-3 gap-4 mb-5">
            <div className="rounded-lg border border-border bg-card p-4 flex items-center gap-3"><span className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center"><Lightning size={17} weight="fill" /></span><div><div className="font-heading text-xl font-semibold">{activeCount}<span className="text-sm text-muted-foreground">/{automations.length}</span></div><div className="text-xs text-muted-foreground">Active automations</div></div></div>
            <div className="rounded-lg border border-border bg-card p-4 flex items-center gap-3"><span className="h-9 w-9 rounded-md bg-success/10 text-success flex items-center justify-center"><PaperPlaneTilt size={17} weight="fill" /></span><div><div className="font-heading text-xl font-semibold">{totalSent.toLocaleString()}</div><div className="text-xs text-muted-foreground">Auto-sent emails</div></div></div>
            <div className="flex items-center justify-end"><NewAutomationDialog templates={templates} segments={segments} /></div>
          </div>

          <div className="space-y-3">
            {automations.length === 0 && <div className="text-center text-muted-foreground py-12 rounded-lg border border-dashed border-border">No automations yet. Create your first lifecycle flow.</div>}
            {automations.map((a, i) => <AutomationCard key={a.id} a={a} i={i} qc={qc} />)}
          </div>
        </TabsContent>

        <TabsContent value="templates">
          <div className="flex justify-between items-center mb-5">
            <p className="text-sm text-muted-foreground">Reusable email templates powering your automations & campaigns.</p>
            <TemplateDialog qc={qc} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((t, i) => <TemplateCard key={t.id} t={t} i={i} qc={qc} />)}
          </div>
        </TabsContent>
      </Tabs>
    </Page>
  );
}

function AutomationCard({ a, i, qc }) {
  const trig = TRIGGERS[a.trigger] || TRIGGERS.reservation_created;
  const toggle = useMutation({
    mutationFn: async () => (await api.post(`/automations/${a.id}/toggle`)).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["automations"] }); toast.success(d.active ? "Automation activated" : "Automation paused"); },
  });
  const run = useMutation({
    mutationFn: async () => (await api.post(`/automations/${a.id}/run`)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["automations"] });
      const c = d.channels || {};
      const parts = [];
      if (c.email) parts.push(`${c.email * d.enrolled} emails`);
      if (c.sms) parts.push(`${c.sms * d.enrolled} SMS`);
      if (c.survey) parts.push(`${c.survey * d.enrolled} surveys`);
      toast.success(`Enrolled ${d.enrolled} guests · ${parts.join(" · ") || `${d.sent} messages`} queued`);
    },
  });
  const del = useMutation({
    mutationFn: async () => (await api.delete(`/automations/${a.id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["automations"] }); toast.success("Automation deleted"); },
  });

  return (
    <div data-testid={`automation-card-${i}`} className="fade-up rounded-lg border border-border bg-card p-5" style={{ animationDelay: `${i * 40}ms` }}>
      <div className="flex items-start gap-4">
        <span className={`h-11 w-11 rounded-lg flex items-center justify-center shrink-0 ${trig.color}`}><trig.icon size={22} weight="fill" /></span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading font-semibold">{a.name}</h3>
            <span className={`text-[11px] px-2 py-0.5 rounded-full border ${a.active ? "bg-success/10 text-success border-success/20" : "bg-secondary text-muted-foreground border-border"}`}>{a.active ? "Active" : "Paused"}</span>
          </div>
          <div className="flex items-center gap-2 mt-1.5 text-sm text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1"><Lightning size={13} weight="fill" className="text-primary" /> {trig.label}</span>
            <span>·</span>
            <span>{a.segment || "All guests"}</span>
            <span>·</span>
            <span>{(a.steps?.length || 1)} step{(a.steps?.length || 1) > 1 ? "s" : ""}</span>
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {(a.steps || []).map((s, si, arr) => {
              const CM = channelMeta[s.channel] || channelMeta.email;
              return (
                <div key={si} className="flex items-center gap-2">
                  <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-2.5 py-1.5">
                    <span className={`h-6 w-6 rounded-full flex items-center justify-center ${CM.color}`}><CM.icon size={13} weight="fill" /></span>
                    <div className="text-xs">
                      <div className="font-medium text-foreground leading-none">{s.label || s.template_name}</div>
                      <div className="text-muted-foreground text-[11px] mt-0.5">{CM.name} · {fmtDelay(s)}</div>
                    </div>
                  </div>
                  {si < arr.length - 1 && <ArrowRight size={14} className="text-muted-foreground shrink-0" />}
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-2.5 text-xs">
            <span className="text-muted-foreground"><span className="font-semibold text-foreground">{(a.sent_count || 0).toLocaleString()}</span> sent</span>
            <span className="text-muted-foreground"><span className="font-semibold text-foreground">{a.enrolled || 0}</span> enrolled</span>
            {a.last_run && <span className="text-muted-foreground">Last run {timeAgo(a.last_run)}</span>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-3 shrink-0">
          <Switch data-testid={`automation-toggle-${i}`} checked={a.active} onCheckedChange={() => toggle.mutate()} />
          <div className="flex items-center gap-1.5">
            <Button data-testid={`automation-run-${i}`} variant="secondary" size="sm" className="gap-1.5 h-8" disabled={run.isPending} onClick={() => run.mutate()}><PaperPlaneTilt size={13} weight="fill" /> Run now</Button>
            <Button data-testid={`automation-delete-${i}`} variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive" onClick={() => del.mutate()}><Trash size={15} /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewAutomationDialog({ templates, segments }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState("reservation_created");
  const [segment, setSegment] = useState("All guests");
  const emptyStep = () => ({ kind: "email", template_id: "", sms_text: "", survey_question: "", delay_value: 0, delay_unit: "days" });
  const [steps, setSteps] = useState([emptyStep()]);

  const reset = () => { setName(""); setTrigger("reservation_created"); setSegment("All guests"); setSteps([emptyStep()]); };
  const addStep = () => setSteps((s) => [...s, { ...emptyStep(), delay_value: 1 }]);
  const removeStep = (i) => setSteps((s) => s.filter((_, idx) => idx !== i));
  const updateStep = (i, patch) => setSteps((s) => s.map((st, idx) => (idx === i ? { ...st, ...patch } : st)));

  const stepValid = (s) => (s.kind === "email" ? !!s.template_id : s.kind === "sms" ? !!s.sms_text.trim() : !!s.survey_question.trim());
  const valid = name.trim() && steps.length && steps.every(stepValid);

  const create = useMutation({
    mutationFn: async () => {
      const payloadSteps = steps.map((s) => {
        const base = { delay_value: Number(s.delay_value) || 0, delay_unit: s.delay_unit };
        if (s.kind === "sms") return { ...base, channel: "sms", type: "message", sms_text: s.sms_text };
        if (s.kind === "survey") return { ...base, channel: "survey", type: "survey", survey_question: s.survey_question, survey_scale: 5 };
        return { ...base, channel: "email", type: "message", template_id: s.template_id };
      });
      return (await api.post("/automations", { name, trigger, segment, steps: payloadSteps })).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["automations"] }); toast.success("Sequence created & activated"); setOpen(false); reset(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not create automation"),
  });

  const kindIcon = { email: EnvelopeSimple, sms: DeviceMobile, survey: Star };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild><Button data-testid="new-automation-btn" className="gap-1.5"><Plus size={16} weight="bold" /> New automation</Button></DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Build a lifecycle sequence</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[13px] font-medium mb-1.5 block">Name</label>
              <input data-testid="automation-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. In-stay comfort check" className="w-full h-10 px-3 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="text-[13px] font-medium mb-1.5 block">Audience</label>
              <Select value={segment} onValueChange={setSegment}>
                <SelectTrigger data-testid="automation-segment"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="All guests">All guests</SelectItem>{segments.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-[13px] font-medium mb-1.5 block">Enrollment trigger</label>
            <Select value={trigger} onValueChange={setTrigger}>
              <SelectTrigger data-testid="automation-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TRIGGERS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">{TRIGGERS[trigger].desc}</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[13px] font-medium">Sequence steps</label>
              <Button data-testid="add-step-btn" type="button" variant="ghost" size="sm" className="gap-1 h-7 text-primary hover:text-primary" onClick={addStep}><Plus size={14} weight="bold" /> Add step</Button>
            </div>
            <div className="space-y-2.5">
              {steps.map((s, i) => {
                const KI = kindIcon[s.kind];
                return (
                  <div key={i} data-testid={`step-row-${i}`} className="rounded-md border border-border p-3 bg-secondary/30 space-y-2.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center shrink-0">{i + 1}</span>
                      <Select value={s.kind} onValueChange={(v) => updateStep(i, { kind: v })}>
                        <SelectTrigger className="w-[150px] h-9" data-testid={`step-kind-${i}`}><KI size={14} className="mr-1" /><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="sms">SMS</SelectItem>
                          <SelectItem value="survey">Survey (1–5)</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-1.5 ml-auto">
                        <input data-testid={`step-delay-${i}`} type="number" min={0} value={s.delay_value} onChange={(e) => updateStep(i, { delay_value: Number(e.target.value) })} className="w-14 h-9 px-2 rounded-md border border-border text-sm text-center outline-none focus:ring-2 focus:ring-primary/30" />
                        <Select value={s.delay_unit} onValueChange={(v) => updateStep(i, { delay_unit: v })}>
                          <SelectTrigger className="w-[92px] h-9" data-testid={`step-unit-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="hours">hours</SelectItem><SelectItem value="days">days</SelectItem></SelectContent>
                        </Select>
                        <span className="text-[11px] text-muted-foreground">after {i === 0 ? "trigger" : "prev"}</span>
                      </div>
                      {steps.length > 1 && <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive shrink-0" data-testid={`remove-step-${i}`} onClick={() => removeStep(i)}><Trash size={14} /></Button>}
                    </div>
                    {s.kind === "email" && (
                      <Select value={s.template_id} onValueChange={(v) => updateStep(i, { template_id: v })}>
                        <SelectTrigger data-testid={`step-template-${i}`}><SelectValue placeholder="Choose email template" /></SelectTrigger>
                        <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                    {s.kind === "sms" && (
                      <input data-testid={`step-sms-${i}`} value={s.sms_text} onChange={(e) => updateStep(i, { sms_text: e.target.value })} placeholder="SMS text, e.g. Hi! Settling in okay? Reply anytime." maxLength={300} className="w-full h-10 px-3 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                    )}
                    {s.kind === "survey" && (
                      <div className="flex items-center gap-2">
                        <input data-testid={`step-survey-${i}`} value={s.survey_question} onChange={(e) => updateStep(i, { survey_question: e.target.value })} placeholder="Survey question, e.g. How is your stay so far?" className="flex-1 h-10 px-3 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                        <div className="flex items-center gap-0.5 text-amber-500 shrink-0" title="Guests rate 1 to 5">
                          {Array.from({ length: 5 }).map((_, j) => <Star key={j} size={15} weight="fill" />)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-2">Mix email, SMS and 1–5 feedback surveys. Sends respect your property's send window (quiet hours) — configure it in Settings. <span className="text-primary">Survey questions & email templates are auto-translated into 8 languages on save.</span></p>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="save-automation-btn" disabled={!valid || create.isPending} onClick={() => create.mutate()}>{create.isPending ? "Creating…" : "Create sequence"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplateCard({ t, i, qc }) {
  const [lang, setLang] = useState("en");
  const [translations, setTranslations] = useState(t.translations || {});
  const [loading, setLoading] = useState(false);
  const isRTL = lang === "ar";

  const view = lang === "en"
    ? { subject: t.subject, body: t.body }
    : (translations[lang] || { subject: t.subject, body: t.body });

  const pickLang = async (code) => {
    setLang(code);
    if (code !== "en" && !translations[code]) {
      setLoading(true);
      try {
        const { data } = await api.post(`/templates/${t.id}/translate`);
        setTranslations(data.translations || {});
        qc.invalidateQueries({ queryKey: ["templates"] });
      } catch { toast.error("Translation failed"); }
      finally { setLoading(false); }
    }
  };

  return (
    <div data-testid={`template-card-${i}`} className="fade-up rounded-lg border border-border bg-card p-5 flex flex-col hover:shadow-md transition-shadow" style={{ animationDelay: `${i * 40}ms` }}>
      <div className="flex items-start justify-between">
        <span className="h-9 w-9 rounded-md bg-secondary flex items-center justify-center text-muted-foreground"><FileText size={17} weight="fill" /></span>
        <span className={`text-[11px] px-2 py-0.5 rounded-full border capitalize ${typeBadge[t.type] || typeBadge.newsletter}`}>{t.type}</span>
      </div>
      <h3 className="font-heading font-semibold mt-3.5">{t.name}</h3>

      <div className="mt-2.5 flex items-center gap-1.5">
        <Translate size={13} className="text-primary" />
        <Select value={lang} onValueChange={pickLang}>
          <SelectTrigger className="h-7 text-xs w-[150px]" data-testid={`template-lang-${i}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.native}</SelectItem>)}
          </SelectContent>
        </Select>
        {loading && <span className="text-[11px] text-muted-foreground">Translating…</span>}
      </div>

      <div dir={isRTL ? "rtl" : "ltr"} className="mt-2.5" data-testid={`template-preview-${i}`}>
        <div className="text-[13px] font-medium text-foreground">{view.subject}</div>
        <p className="text-xs text-muted-foreground mt-1.5 line-clamp-3 whitespace-pre-line">{view.body}</p>
      </div>

      <div className="mt-3 flex-1 flex items-end"><TemplateDialog qc={qc} template={t} index={i} /></div>
    </div>
  );
}

function TemplateDialog({ qc, template, index }) {
  const isEdit = !!template;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(template || { name: "", type: "promotion", subject: "", body: "" });
  const [prompt, setPrompt] = useState("");
  const [gen, setGen] = useState(false);

  const generate = async () => {
    if (!prompt.trim()) return toast.info("Describe the email first");
    setGen(true);
    try {
      const { data } = await api.post("/ai/generate-email", { campaign_type: form.type, prompt });
      setForm((f) => ({ ...f, subject: data.subject, body: data.body }));
      toast.success("Claude drafted your template");
    } catch { toast.error("AI generation failed"); }
    finally { setGen(false); }
  };

  const save = useMutation({
    mutationFn: async () => {
      const payload = { name: form.name, type: form.type, subject: form.subject, body: form.body };
      return isEdit ? (await api.put(`/templates/${template.id}`, payload)).data : (await api.post("/templates", payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["templates"] }); toast.success(isEdit ? "Template updated" : "Template created"); setOpen(false); if (!isEdit) setForm({ name: "", type: "promotion", subject: "", body: "" }); },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o && template) setForm(template); }}>
      <DialogTrigger asChild>
        {isEdit
          ? <Button data-testid={`edit-template-${index}`} variant="secondary" size="sm" className="gap-1.5 w-full"><PencilSimple size={14} /> Edit template</Button>
          : <Button data-testid="new-template-btn" className="gap-1.5"><Plus size={16} weight="bold" /> New template</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{isEdit ? "Edit template" : "Create template"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 md:col-span-1">
            <label className="text-[13px] font-medium mb-1.5 block">Name</label>
            <input data-testid="template-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-10 px-3 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div className="col-span-2 md:col-span-1">
            <label className="text-[13px] font-medium mb-1.5 block">Type</label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="template-type"><SelectValue /></SelectTrigger>
              <SelectContent>{["pre-arrival", "post-stay", "win-back", "birthday", "promotion", "newsletter"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2 rounded-md border border-primary/20 bg-primary/5 p-3">
            <label className="text-[13px] font-medium mb-1.5 flex items-center gap-1.5 text-primary"><Sparkle size={15} weight="fill" /> AI content assistant</label>
            <div className="flex gap-2">
              <input data-testid="template-ai-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. cheerful birthday note with a free upgrade" className="flex-1 h-10 px-3 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              <Button data-testid="template-ai-generate" type="button" variant="secondary" disabled={gen} onClick={generate} className="gap-1.5 shrink-0">{gen ? "Drafting…" : <><Sparkle size={15} weight="fill" /> Generate</>}</Button>
            </div>
          </div>
          <div className="col-span-2">
            <label className="text-[13px] font-medium mb-1.5 block">Subject</label>
            <input data-testid="template-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full h-10 px-3 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div className="col-span-2">
            <label className="text-[13px] font-medium mb-1.5 block">Body</label>
            <textarea data-testid="template-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={6} className="w-full px-3 py-2.5 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="save-template-btn" disabled={!form.name || !form.subject || save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Saving…" : isEdit ? "Save changes" : "Create template"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
